import * as React from 'react';
import { useTranslation } from 'next-i18next';
import { Controller } from 'react-hook-form';

import {
  Button as ButtonV2,
  EmailInput,
  TextInput,
  PhoneInput,
  RadioGroup,
  Heading,
  FormLabelWrapper,
  isValidEmail,
  isValidPhoneNumber,
} from '@restworld/ui-ds-base';

import useCollaboratorDetailsForm, {
  AddCollaboratorSteps,
  CollaboratorDetailsFormProps,
} from '../../../hooks/use-collaborator-details-form';
import { CollaboratorModalFooterContext } from '../collaborator-modal-footer-context';
import {
  StyledForm,
  ButtonsWrapper,
  FooterButtonsWrapper,
  ContentAndInputsWrapper,
  ContentWrapper,
  InputAndLabelWrapper,
  InputWrapper,
  InputsGridWrapper,
  InputsWrapper,
} from './collaborator-details-form.styled';
import usePermissionLevelsList from '../../../hooks/use-collaborator-permission-level-list';
import useCollaboratorFormTitles from '../../../hooks/use-collaborator-form-titles';

function CollaboratorDetailsForm({ setStep, step }: CollaboratorDetailsFormProps) {
  const { t } = useTranslation('collaborators-page');
  const footerContext = React.useContext(CollaboratorModalFooterContext);

  const permissionLevels = usePermissionLevelsList();
  const { formMainTitle, formSubtitleEl } = useCollaboratorFormTitles(step);
  const {
    canSubmit,
    control,
    errors,
    handleCancel,
    handleContinue,
    isPending,
    hasConflictingEmailError,
    hasConflictingphoneNumberError,
    handleSubmitForm,
  } = useCollaboratorDetailsForm({ setStep, step });

  const footerEl = React.useMemo(
    () => (
      <FooterButtonsWrapper>
        <ButtonV2 type="button" variant="tertiary" onClick={handleCancel}>
          {t('cancel')}
        </ButtonV2>
        <ButtonV2 type="button" onClick={canSubmit ? handleSubmitForm : handleContinue} loading={isPending}>
          {t('next')}
        </ButtonV2>
      </FooterButtonsWrapper>
    ),
    [canSubmit, handleCancel, handleContinue, handleSubmitForm, isPending, t]
  );

  React.useEffect(() => {
    if (footerContext) {
      footerContext.setFooter(footerEl);
      return () => footerContext.setFooter(null);
    }
  }, [footerContext, footerEl]);

  return (
    <StyledForm>
      <ContentAndInputsWrapper>
        <Heading color="neutral" className="w-full text-base lg:text-lg">
          {formMainTitle}
        </Heading>
        <ContentWrapper>{formSubtitleEl}</ContentWrapper>
        <InputsWrapper>
          <InputWrapper isHidden={step !== AddCollaboratorSteps.FILL_INFO}>
            <InputsGridWrapper>
              <InputAndLabelWrapper>
                <FormLabelWrapper label={t('name')} isFullWidth>
                  <Controller
                    name="name"
                    control={control}
                    rules={{ required: t('name_is_required') }}
                    render={({ field }) => (
                      <TextInput
                        type="text"
                        id="name"
                        bordercolor="neutral-100"
                        placeholder={t('insert_the_name')}
                        isError={!!errors['name']}
                        supportText={errors['name']?.message || ''}
                        {...field}
                      />
                    )}
                  />
                </FormLabelWrapper>
              </InputAndLabelWrapper>
              <InputAndLabelWrapper>
                <FormLabelWrapper label={t('surname')}>
                  <Controller
                    name="surname"
                    control={control}
                    rules={{ required: t('surname_is_required') }}
                    render={({ field }) => (
                      <TextInput
                        type="text"
                        id="surname"
                        bordercolor="neutral-100"
                        isError={!!errors['surname']}
                        placeholder={t('insert_the_surname')}
                        supportText={errors['surname']?.message || ''}
                        {...field}
                      />
                    )}
                  />
                </FormLabelWrapper>
              </InputAndLabelWrapper>
              <InputAndLabelWrapper>
                <FormLabelWrapper label={t('email')}>
                  <Controller
                    name="email"
                    control={control}
                    rules={{
                      required: t('email_is_required'),
                      validate: (value) => isValidEmail(value) || t('insert_a_valid_email'),
                    }}
                    render={({ field }) => (
                      <EmailInput
                        id="email"
                        variant={errors['email'] || hasConflictingEmailError ? 'error' : 'primary'}
                        supportText={
                          errors['email']?.message ||
                          (hasConflictingEmailError ? t('email_has_been_used_by_another_user') : '')
                        }
                        placeholder={t('insert_the_email_address')}
                        {...field}
                      />
                    )}
                  />
                </FormLabelWrapper>
              </InputAndLabelWrapper>
              <InputAndLabelWrapper>
                <FormLabelWrapper label={t('phone')}>
                  <Controller
                    name="phoneNumber"
                    control={control}
                    rules={{
                      validate: (phoneNumber) => isValidPhoneNumber(phoneNumber) || t('insert_valid_phone_number'),
                    }}
                    render={({ field }) => (
                      <PhoneInput
                        id="phoneNumber"
                        inputContainerStyles={['border-neutral-100 border']}
                        variant={errors['phoneNumber'] || hasConflictingphoneNumberError ? 'error' : 'primary'}
                        supportText={
                          errors['phoneNumber']?.message ||
                          (hasConflictingphoneNumberError ? t('phone_number_has_been_used_by_another_user') : '')
                        }
                        placeholder={t('insert_the_phone_number')}
                        {...field}
                      />
                    )}
                  />
                </FormLabelWrapper>
              </InputAndLabelWrapper>
            </InputsGridWrapper>
          </InputWrapper>
          {step === AddCollaboratorSteps.CHOOSE_PERMISSION_LEVEL && (
            <InputWrapper>
              <Controller
                name="permissionLevel"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <RadioGroup
                    options={permissionLevels}
                    id="permissionLevel"
                    orientation="horizontal"
                    helperText={errors['permissionLevel'] ? t('permission_level_is_required') : ''}
                    {...field}
                  />
                )}
              />
            </InputWrapper>
          )}
        </InputsWrapper>
      </ContentAndInputsWrapper>
      {!footerContext && (
        <ButtonsWrapper>
          <ButtonV2 type="button" variant="tertiary" onClick={handleCancel}>
            {t('cancel')}
          </ButtonV2>
          <ButtonV2 type="button" onClick={canSubmit ? handleSubmitForm : handleContinue} loading={isPending}>
            {t('next')}
          </ButtonV2>
        </ButtonsWrapper>
      )}
    </StyledForm>
  );
}
export default CollaboratorDetailsForm;
